// Lightweight, fail-soft analytics tracking helper.
//
// Posts funnel/onboarding events to POST /api/analytics/events. It must NEVER
// throw into the UI — every failure is swallowed and logged. The backend now
// accepts question-agnostic event types (onboarding_*) with a free-form
// `properties` blob and an optional questionId.
//
// UTM attribution is automatically merged into every event's properties.utm
// field — callers do not need to pass it manually.

import axios from 'axios';
import { getUtm } from './utm';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const SESSION_STORAGE_KEY = 'cleveroo_analytics_session_id';
const ANON_STORAGE_KEY = 'cleveroo_analytics_anon_id';

function randomId(prefix: string): string {
  const rnd =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${rnd}`;
}

/** Stable per-tab/session id, reused across events in the same session. */
export function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = randomId('sess');
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    // sessionStorage unavailable (private mode / SSR) — best-effort id.
    return randomId('sess');
  }
}

/** Stable anonymous id used when there is no authenticated user. */
function getAnonId(): string {
  try {
    let id = localStorage.getItem(ANON_STORAGE_KEY);
    if (!id) {
      id = randomId('anon');
      localStorage.setItem(ANON_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return randomId('anon');
  }
}

function authHeaders(): Record<string, string> {
  try {
    const token =
      localStorage.getItem('cleveroo_token') ||
      localStorage.getItem('accessToken') ||
      '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export interface TrackOptions {
  /** Authenticated user id, if known. Falls back to a stable anonymous id. */
  userId?: string | null;
  /** Override the session id (defaults to the per-session id). */
  sessionId?: string;
  /** Free-form event properties. */
  properties?: Record<string, any>;
}

/**
 * Fire an analytics event. Fail-soft: resolves to void and never throws.
 * UTM attribution is automatically merged into properties.utm on every call.
 */
export async function track(
  eventType: string,
  options: TrackOptions = {}
): Promise<void> {
  try {
    const utm = getUtm();
    const payload = {
      eventType,
      userId: options.userId || getAnonId(),
      sessionId: options.sessionId || getSessionId(),
      properties: {
        ...(options.properties || {}),
        utm,
      },
    };

    await axios.post(`${API_BASE_URL}/api/analytics/events`, payload, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      // Keep tracking snappy and non-blocking.
      timeout: 5000,
    });
  } catch (err) {
    // Swallow — analytics must never break the UX.
    // eslint-disable-next-line no-console
    console.warn('[analytics] track failed', eventType, err);
  }
}

/**
 * Fire a paid-conversion event. Call this from the upgrade / checkout-success
 * handler once a payment is confirmed.
 *
 * @param plan   - e.g. 'monthly', 'annual', 'trial-upgrade'
 * @param amount - revenue amount in the account's currency (e.g. 29.99)
 */
export async function trackPaidConversion(
  plan?: string,
  amount?: number
): Promise<void> {
  // TODO: wire this call into the upgrade / subscription-success handler once
  // the paywall / checkout flow is built. Quick grep shows no existing
  // upgrade/subscribe/checkout/paid handler in cleveroo-react at time of
  // writing (KAN-14, 2026-06-16).
  return track('paidConversion', {
    properties: {
      ...(plan ? { plan } : {}),
      ...(amount !== undefined ? { amount } : {}),
    },
  });
}
